/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { PATHS } from '../../router/paths';
import toast from 'react-hot-toast';
import {
  Building2,
  Users,
  ArrowLeft,
  Mail,
  Calendar,
  CreditCard,
  PencilLine,
  Trash2,
  Hash,
  Clock,
  Shield,
  BarChart3
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { StatusBadge, Badge } from '@components/atoms/ui-kit/badge';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import { useGetAdminOrganization } from '@utils/api/admin/use-get-admin-organization';
import { useDeleteAdminOrganization } from '@utils/api/admin/use-delete-admin-organization';
import OrganizationSubscriptionTab from './organization-subscription-tab';
import OrganizationUsersTab from './organization-users-tab';
import OrganizationUsageTab from './organization-usage-tab';
import OrganizationProfileEdit from './organization-profile-edit';

type TabId = 'profile' | 'users' | 'subscription' | 'usage';

/**
 * On-Prem Admin Organization Detail
 * Shows organization details with tabs: Profile, Users, Subscription
 */
export default function AdminOrganizationDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const {
    data: orgData,
    isLoading: orgLoading,
    refetch
  } = useGetAdminOrganization(organizationId ?? '');
  const deleteMutation = useDeleteAdminOrganization();

  const organization = orgData?.result;

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaved = () => {
    setIsEditing(false);
    refetch();
  };

  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (organizationId) {
      deleteMutation.mutate(organizationId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          navigate(PATHS().ADMIN_ORGANIZATIONS);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      });
    }
  };

  const tabs: { id: TabId; label: string; icon: typeof Building2 }[] = [
    { id: 'profile', label: t('profile'), icon: Building2 },
    { id: 'users', label: t('users'), icon: Users },
    { id: 'subscription', label: t('subscription'), icon: CreditCard },
    { id: 'usage', label: t('usage'), icon: BarChart3 }
  ];

  if (orgLoading) {
    return (
      <div className="bg-app min-h-screen px-4 py-2">
        <Skeleton variant="card" className="mt-8" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="px-4 py-2">
        <p style={{ color: getColour('normalText') }}>
          {t('organization-not-found')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      {/* Back button */}
      <button
        onClick={() => navigate(PATHS().ADMIN_ORGANIZATIONS)}
        className="flex items-center gap-2 mb-4 text-sm hover:opacity-80"
        style={{ color: getColour('inactiveText') }}
      >
        <ArrowLeft className="h-4 w-4" />
        {t('back-to-organizations')}
      </button>

      <PageHeader title={organization.name} caption={organization.email}>
        {activeTab === 'profile' && (
          <Button
            icon={<Trash2 width={14} />}
            onClick={handleDelete}
            label={t('delete')}
            variant="danger"
            size="small"
          />
        )}
      </PageHeader>

      {/* Tabs */}
      <div
        className="flex gap-1 mb-6 border-b"
        style={{ borderColor: getColour('border') }}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors"
            style={{
              color:
                activeTab === tab.id
                  ? getColour('activeText')
                  : getColour('inactiveText'),
              borderBottom:
                activeTab === tab.id
                  ? `2px solid ${getColour('activeText')}`
                  : '2px solid transparent'
            }}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        className="rounded-2xl border p-6"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('secondLayer')
        }}
      >
        {/* Profile Tab */}
        {activeTab === 'profile' &&
          (isEditing ? (
            <OrganizationProfileEdit
              organization={organization}
              onCancel={handleCancelEdit}
              onSaved={handleSaved}
            />
          ) : (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    <Hash className="inline h-3 w-3 mr-1" />
                    {t('id')}
                  </label>
                  <p
                    className="text-sm"
                    style={{ color: getColour('normalText') }}
                  >
                    {organization.id}
                  </p>
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('organization-name')}
                  </label>
                  <p
                    className="text-sm"
                    style={{ color: getColour('normalText') }}
                  >
                    {organization.name}
                  </p>
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    <Mail className="inline h-3 w-3 mr-1" />
                    {t('email')}
                  </label>
                  <p
                    className="text-sm"
                    style={{ color: getColour('normalText') }}
                  >
                    {organization.email}
                  </p>
                </div>
                {organization.phone && (
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('phone')}
                    </label>
                    <p
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {organization.phone}
                    </p>
                  </div>
                )}
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('status')}
                  </label>
                  <StatusBadge
                    status={organization.is_active ? 'active' : 'inactive'}
                    label={organization.is_active ? t('active') : t('inactive')}
                  />
                </div>
                {organization.is_reseller && (
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      <Shield className="inline h-3 w-3 mr-1" />
                      {t('type')}
                    </label>
                    <Badge color="blue">{t('reseller')}</Badge>
                  </div>
                )}
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    <Calendar className="inline h-3 w-3 mr-1" />
                    {t('created-at')}
                  </label>
                  <p
                    className="text-sm"
                    style={{ color: getColour('normalText') }}
                  >
                    {new Date(organization.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Parent Organization (if applicable) */}
              {organization.parent_organization_id && (
                <div
                  className="pt-4 border-t"
                  style={{ borderColor: getColour('border') }}
                >
                  <h3
                    className="text-sm font-semibold mb-4"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('parent-organization')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      className="p-3 rounded-2xl"
                      style={{ backgroundColor: getColour('thirdLayer') }}
                    >
                      <label
                        className="block text-xs uppercase tracking-wide mb-1"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('parent-id')}
                      </label>
                      <p
                        className="text-sm font-medium"
                        style={{ color: getColour('normalText') }}
                      >
                        {organization.parent_organization_id}
                      </p>
                    </div>
                    {organization.parent_organization_name && (
                      <div
                        className="p-3 rounded-2xl"
                        style={{ backgroundColor: getColour('thirdLayer') }}
                      >
                        <label
                          className="block text-xs uppercase tracking-wide mb-1"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t('parent-name')}
                        </label>
                        <p
                          className="text-sm font-medium"
                          style={{ color: getColour('normalText') }}
                        >
                          {organization.parent_organization_name}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Settings (editable fields shown as read-only) */}
              <div
                className="pt-4 border-t"
                style={{ borderColor: getColour('border') }}
              >
                <h3
                  className="text-sm font-semibold mb-4"
                  style={{ color: getColour('normalText') }}
                >
                  {t('settings')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div
                    className="p-3 rounded-2xl"
                    style={{ backgroundColor: getColour('thirdLayer') }}
                  >
                    <label
                      className="block text-xs uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      <Clock className="inline h-3 w-3 mr-1" />
                      {t('retention-days')}
                    </label>
                    <p
                      className="text-sm font-medium"
                      style={{ color: getColour('normalText') }}
                    >
                      {organization.retention_days} {t('days')}
                    </p>
                  </div>
                  <div
                    className="p-3 rounded-2xl"
                    style={{ backgroundColor: getColour('thirdLayer') }}
                  >
                    <label
                      className="block text-xs uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      <Users className="inline h-3 w-3 mr-1" />
                      {t('max-users')}
                    </label>
                    <p
                      className="text-sm font-medium"
                      style={{ color: getColour('normalText') }}
                    >
                      {organization.max_users}
                    </p>
                  </div>
                  <div
                    className="p-3 rounded-2xl"
                    style={{ backgroundColor: getColour('thirdLayer') }}
                  >
                    <label
                      className="block text-xs uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('batch-priority')}
                    </label>
                    <p
                      className="text-sm font-medium"
                      style={{ color: getColour('normalText') }}
                    >
                      {organization.batch_priority}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats (read-only) */}
              {(organization.user_count !== undefined ||
                organization.session_count !== undefined) && (
                <div
                  className="pt-4 border-t"
                  style={{ borderColor: getColour('border') }}
                >
                  <h3
                    className="text-sm font-semibold mb-4"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('statistics')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {organization.user_count !== undefined && (
                      <div
                        className="p-3 rounded-2xl"
                        style={{ backgroundColor: getColour('thirdLayer') }}
                      >
                        <label
                          className="block text-xs uppercase tracking-wide mb-1"
                          style={{ color: getColour('inactiveText') }}
                        >
                          <Users className="inline h-3 w-3 mr-1" />
                          {t('user-count')}
                        </label>
                        <p
                          className="text-sm font-medium"
                          style={{ color: getColour('normalText') }}
                        >
                          {organization.user_count}
                        </p>
                      </div>
                    )}
                    {organization.session_count !== undefined && (
                      <div
                        className="p-3 rounded-2xl"
                        style={{ backgroundColor: getColour('thirdLayer') }}
                      >
                        <label
                          className="block text-xs uppercase tracking-wide mb-1"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t('session-count')}
                        </label>
                        <p
                          className="text-sm font-medium"
                          style={{ color: getColour('normalText') }}
                        >
                          {organization.session_count}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Edit Button */}
              <div
                className="flex justify-end pt-4 border-t"
                style={{ borderColor: getColour('border') }}
              >
                <Button
                  icon={<PencilLine width={14} />}
                  onClick={handleEdit}
                  label={t('edit')}
                  variant="secondary"
                  size="small"
                />
              </div>
            </div>
          ))}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <OrganizationUsersTab organizationId={organizationId ?? ''} />
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <OrganizationSubscriptionTab organizationId={organizationId ?? ''} />
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <OrganizationUsageTab organizationId={organizationId ?? ''} />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        size="small"
        title={t('delete-organization')}
        caption={t('are-you-sure-you-want-to-delete-this-organization')}
        confirmLabel={deleteMutation.isLoading ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        confirmDisabled={deleteMutation.isLoading}
        destructive
        autoFocusConfirm
      />
    </div>
  );
}
